import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Download, TrendingUp, Filter, Search } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { useUser } from '../context/UserContext';

const History = () => {
  const navigate = useNavigate();
  const { 
    user: contextUser, 
    history: userHistory,
    transferHistory: userTransferHistory
  } = useUser();
  
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'sent', 'received', 'deposit', 'withdrawal'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    setUser(contextUser);
  }, [contextUser, navigate]);

  // Combine all transactions from history and transfers
  const getAllTransactions = () => {
    const transactions = [];

    // Add deposit/withdrawal transactions
    userHistory.forEach(item => {
      transactions.push({
        id: item._id,
        type: item.transactionType,
        description: item.transactionType === 'deposit' ? 'Deposit' : 'Withdrawal',
        amount: item.amount,
        date: item.date,
        status: item.transactionProcess,
        transactionID: item.transactionID
      });
    });

    // Add transfers
    userTransferHistory.forEach(transfer => {
      if (transfer.sender === user?.telegramName) {
        transactions.push({
          id: transfer._id,
          type: 'sent',
          description: `Sent to ${transfer.receiver}`,
          recipient: transfer.receiver,
          amount: transfer.amount,
          date: transfer.date,
          status: transfer.transferPrecess,
          transactionID: transfer.transferID
        });
      } else if (transfer.receiver === user?.telegramName) {
        transactions.push({
          id: transfer._id,
          type: 'received',
          description: `Received from ${transfer.sender}`,
          sender: transfer.sender,
          amount: transfer.amount,
          date: transfer.date,
          status: transfer.transferPrecess,
          transactionID: transfer.transferID
        });
      }
    });

    // Sort by date (newest first)
    return transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getFilteredTransactions = () => {
    let filtered = getAllTransactions();

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter(tx => tx.type === filter);
    }

    // Search
    if (searchQuery) {
      filtered = filtered.filter(tx => 
        tx.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.recipient?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.sender?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.transactionID?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const getTransactionIcon = (type) => {
    switch(type) {
      case 'sent':
      case 'debit':
        return <Send className="text-red-600" size={20} />;
      case 'received':
      case 'credit':
        return <Download className="text-green-600" size={20} />;
      case 'investment':
        return <TrendingUp className="text-blue-600" size={20} />;
      default:
        return <Send className="text-slate-600" size={20} />;
    }
  };

  const getTransactionColor = (type) => {
    switch(type) {
      case 'sent':
      case 'debit':
        return 'text-red-600';
      case 'received':
      case 'credit':
        return 'text-green-600';
      default:
        return 'text-slate-900';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const filteredTransactions = getFilteredTransactions();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-lg font-semibold text-slate-900">Transaction History</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters & Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('sent')}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'sent'
                  ? 'bg-red-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Sent
            </button>
            <button
              onClick={() => setFilter('received')}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'received'
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Received
            </button>
            <button
              onClick={() => setFilter('investment')}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'investment'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Investment
            </button>
          </div>
        </div>

        {/* Transaction List */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          {filteredTransactions.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {filteredTransactions.map((transaction, idx) => (
                <div key={idx} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        transaction.type === 'sent' || transaction.type === 'debit'
                          ? 'bg-red-100'
                          : transaction.type === 'received' || transaction.type === 'credit'
                          ? 'bg-green-100'
                          : 'bg-blue-100'
                      }`}>
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {transaction.description || 'Transaction'}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(transaction.date) || 'Recent'}
                        </p>
                        {(transaction.recipient || transaction.sender) && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {transaction.recipient ? `To: ${transaction.recipient}` : `From: ${transaction.sender}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${getTransactionColor(transaction.type)}`}>
                        {(transaction.type === 'sent' || transaction.type === 'debit') ? '-' : '+'}
                        {formatCurrency(transaction.amount)}
                      </p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        transaction.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : transaction.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {transaction.status || 'Completed'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Filter className="mx-auto mb-4 text-slate-300" size={48} />
              <p className="text-slate-600 font-medium">No transactions found</p>
              <p className="text-sm text-slate-400 mt-1">
                {searchQuery ? 'Try adjusting your search' : 'Start making transactions to see your history'}
              </p>
            </div>
          )}
        </div>

        {/* Summary Card */}
        {filteredTransactions.length > 0 && (
          <div className="mt-6 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl p-6 text-white">
            <h3 className="text-lg font-bold mb-4">Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-slate-300 text-sm mb-1">Total Transactions</p>
                <p className="text-2xl font-bold">{filteredTransactions.length}</p>
              </div>
              <div>
                <p className="text-slate-300 text-sm mb-1">Total Sent</p>
                <p className="text-2xl font-bold text-red-400">
                  {filteredTransactions.filter(t => t.type === 'sent' || t.type === 'debit' || t.type === 'withdrawal').length}
                </p>
              </div>
              <div>
                <p className="text-slate-300 text-sm mb-1">Total Received</p>
                <p className="text-2xl font-bold text-green-400">
                  {filteredTransactions.filter(t => t.type === 'received' || t.type === 'credit' || t.type === 'deposit').length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
